import React, { useState, useRef, useEffect } from 'react';
import { useStore } from '../store/useStore';
import {
  passwordLogin, fetchAuthMe,
  setAuthToken, setCurrentUserId,
} from '../utils/api';
import styles from './SignInScreen.module.css';

// Single sign-in mode: email + password.
//
// New users don't sign up here — admins invite them via the Admin
// Console (POST /api/users), which sends an invitation email with a
// one-click link to the AccountSetup flow. If a user can't sign in
// (forgot password, account locked, etc.), they ask the admin to
// resend the invite — admin clicks "Resend Invite" in the users
// table, the user gets a fresh email, and walks back through
// AccountSetup with a new password.
//
// The hash-fragment handler (#token=<jwt>) below stays — that's how
// the magic-link verify flow lands the user back here after the
// confirm interstitial. SignInScreen catches it, populates the JWT
// in localStorage, fetches /me, and App.jsx routes them to
// AccountSetupPage if password_set is false (first-time / reset)
// or to the projects page otherwise.

const ERROR_MESSAGES = {
  'invalid-token':   "That invitation link isn't valid. Ask your admin to resend it.",
  'expired-token':   'That link expired. Ask your admin to resend a fresh one.',
  'used-token':      'That link has already been used. Ask your admin to resend if you need a new one.',
  'missing-token':   'No sign-in token in the URL.',
};

export default function SignInScreen() {
  const setSignedIn    = useStore((s) => s.setSignedIn);
  const setCurrentUser = useStore((s) => s.setCurrentUser);

  const [email,   setEmail]   = useState(() => localStorage.getItem('cf.lastEmail') || '');
  const [pw,      setPw]      = useState('');
  const [busy,    setBusy]    = useState(false);
  const [error,   setError]   = useState('');
  const [verifying, setVerifying] = useState(false);
  const inputRef = useRef(null);
  const pwRef    = useRef(null);

  // Magic-link / invite landing handler. The verify POST redirects to
  // /login#token=<jwt>; we read the hash, store the JWT, and let
  // App.jsx route to AccountSetupPage if password_set is false.
  useEffect(() => {
    const url = new URL(window.location.href);
    const errParam = url.searchParams.get('error');
    if (errParam) {
      setError(ERROR_MESSAGES[errParam] || 'Sign-in failed.');
      url.searchParams.delete('error');
      window.history.replaceState({}, '', url.toString());
      return;
    }

    const hash = window.location.hash || '';
    const m = hash.match(/[#&]token=([^&]+)/);
    if (!m) return;

    const token = decodeURIComponent(m[1]);
    setVerifying(true);
    window.history.replaceState({}, '', url.pathname + url.search);

    setAuthToken(token);
    fetchAuthMe()
      .then((me) => {
        setCurrentUser(me);
        setCurrentUserId(me.id);
        setSignedIn(true);
      })
      .catch((err) => {
        setAuthToken(null);
        setError(err.message || 'Could not verify session. Ask your admin to resend the invite.');
        setVerifying(false);
      });
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (verifying) return;
    // Skip auto-focus on touch devices: it pops the keyboard on load,
    // which shrinks the viewport and pushes the card out of view.
    if (window.matchMedia?.('(pointer: coarse)').matches) return;
    if (email) pwRef.current?.focus();
    else       inputRef.current?.focus();
    // email read once on mount: if localStorage had a saved email,
    // skip ahead to the password field. Re-running on every keystroke
    // would yank focus mid-type.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verifying]);

  async function submit(e) {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      const trimmed = email.trim().toLowerCase();
      localStorage.setItem('cf.lastEmail', trimmed);
      const { token } = await passwordLogin({ email: trimmed, password: pw });
      setAuthToken(token);
      const me = await fetchAuthMe();
      setCurrentUser(me);
      setCurrentUserId(me.id);
      setSignedIn(true);
    } catch (err) {
      const msg = err.message || '';
      if (/password not set/i.test(msg)) {
        setError("Your account isn't fully set up yet. Check your inbox for the invitation email, or ask your admin to resend it.");
      } else if (/invalid credentials/i.test(msg)) {
        setError('Wrong email or password. If you forgot your password, ask your admin to resend your invitation.');
      } else {
        setError(msg || 'Sign-in failed.');
      }
    } finally {
      setBusy(false);
    }
  }

  if (verifying) {
    return (
      <div className={styles.screen}>
        <div className={styles.card}>
          <div className={styles.logo}>GUMBOGRID</div>
          <div className={styles.subtitle}>Verifying invitation…</div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.screen}>
      <form className={styles.card} onSubmit={submit}>
        <div className={styles.logo}>GUMBOGRID</div>
        <div className={styles.subtitle}>SIGN IN</div>

        <label className={styles.label} htmlFor="signin-email">EMAIL</label>
        <input
          id="signin-email"
          ref={inputRef}
          type="email"
          className={styles.input}
          value={email}
          onChange={(e) => { setEmail(e.target.value); if (error) setError(''); }}
          placeholder="you@company.com"
          autoComplete="email"
          spellCheck={false}
          required
        />

        <label className={styles.label} htmlFor="signin-pw">PASSWORD</label>
        <input
          id="signin-pw"
          ref={pwRef}
          type="password"
          className={styles.input}
          value={pw}
          onChange={(e) => { setPw(e.target.value); if (error) setError(''); }}
          autoComplete="current-password"
          required
        />

        {error && <div className={styles.error}>{error}</div>}

        <button
          type="submit"
          className={styles.submit}
          disabled={busy || !email.trim() || !pw}
        >
          {busy ? 'SIGNING IN…' : 'SIGN IN →'}
        </button>

        <div className={styles.footnote}>
          New here? Ask your admin to send you an invitation email — it has
          a one-click setup link. If you forgot your password, an admin can
          resend the invite to reset it.
        </div>
      </form>
    </div>
  );
}
